// Define component
const RugCanvas = {
    name: 'RugCanvas',
    props: {
        size: {
            type: Object,
            required: true
        }
    },
    setup(props) {
        const { ref, onMounted, watch, onUnmounted } = Vue;

        const canvas = ref(null);
        const ctx = ref(null);
        const roomWidth = ref(null);
        const roomLength = ref(null);
        const gridSize = ref(0.5);
        const dimensionsOverlay = ref(true);
        const roomOffset = ref({ x: 0, y: 0 });
        const roomPixelSize = ref(0);
        const rug = ref(null);
        const selectedRug = ref(props.size);
        const isDragging = ref(false);
        const dragOffset = ref({ x: 0, y: 0 });
        const autoAdjusted = ref(false);

        // Utility functions
        const cmToMeters = (cm) => cm / 100;

        // Function to check if rug fits in current room orientation
        const doesRugFitInRoom = () => {
            if (!rug.value) return true;
            
            const rugWidthM = cmToMeters(rug.value.width);
            const rugLengthM = cmToMeters(rug.value.length);
            
            return rugWidthM <= roomWidth.value && rugLengthM <= roomLength.value;
        };

        // Function to check if rug would fit if room was rotated
        const wouldRugFitInRotatedRoom = () => {
            if (!rug.value) return false;
            
            const rugWidthM = cmToMeters(rug.value.width);
            const rugLengthM = cmToMeters(rug.value.length);
            
            return rugWidthM <= roomLength.value && rugLengthM <= roomWidth.value;
        };
        
        // Function to auto-adjust room orientation if needed to fit the rug
        const autoAdjustRoomOrientation = () => {
            if (!rug.value) return;
            
            // If rug already fits, do nothing
            if (doesRugFitInRoom()) {
                autoAdjusted.value = false;
                return;
            }
            
            // Check if rug would fit if room was rotated
            if (wouldRugFitInRotatedRoom()) {
                // Rotate the room to fit the rug
                rotateRoom();
                autoAdjusted.value = true;
                return;
            }
            
            // If we get here, rug won't fit no matter what, so we keep current orientation
            autoAdjusted.value = false;
        };

        // Canvas management functions
        const updateCanvasSize = () => {
            if (!canvas.value) return;

            if (roomWidth.value > 1000) {
                roomWidth.value = 1000;
            }
            if (roomLength.value > 1000) {
                roomLength.value = 1000;
            }

            // Make canvas fill parent width and fixed height
            canvas.value.width = canvas.value.offsetWidth;
            canvas.value.height = 400;

            // Calculate room dimensions while maintaining aspect ratio
            const canvasPadding = 40; // Padding from canvas edges
            const availableWidth = canvas.value.width - canvasPadding * 2;
            const availableHeight = canvas.value.height - canvasPadding * 2;

            // Calculate scale based on room aspect ratio
            const roomAspectRatio = roomLength.value / roomWidth.value;

            if (roomAspectRatio > availableHeight / availableWidth) {
                // Room is taller than wide - fit to height
                roomPixelSize.value = availableHeight / roomAspectRatio;
            } else {
                // Room is wider than tall - fit to width
                roomPixelSize.value = availableWidth;
            }

            // Center the room
            roomOffset.value = {
                x: (canvas.value.width - roomPixelSize.value) / 2,
                y: (canvas.value.height - (roomPixelSize.value * roomAspectRatio)) / 2
            };

            render();
        };

        const render = () => {
            if (!ctx.value) return;

            // Clear canvas
            ctx.value.clearRect(0, 0, canvas.value.width, canvas.value.height);

            // Draw background
            drawBackground();

            // Draw room
            drawRoom();
            drawGrid();

            // Draw rug if it exists
            if (rug.value) {
                drawRug(rug.value);
            }
        };

        // Drawing functions
        const drawBackground = () => {
            ctx.value.fillStyle = '#F2F4F8'; // Set your desired background color
            ctx.value.fillRect(0, 0, canvas.value.width, canvas.value.height);
        };

        const drawRoom = () => {
            ctx.value.strokeStyle = '#333';
            ctx.value.lineWidth = 2;
            ctx.value.strokeRect(
                roomOffset.value.x,
                roomOffset.value.y,
                roomPixelSize.value,
                roomPixelSize.value * (roomLength.value / roomWidth.value) // Adjust height based on ratio
            );
        };

        const drawGrid = () => {
            // Set grid size to 0.25 meters
            const gridSize = 0.25;

            // Calculate pixels per meter
            const pixelsPerMeter = roomPixelSize.value / roomWidth.value;
            const gridPixelSize = gridSize * pixelsPerMeter;

            ctx.value.strokeStyle = '#CBD3E4';
            ctx.value.lineWidth = 0.5;

            // Draw vertical lines
            for (let x = 0; x <= roomWidth.value; x += gridSize) {
                const pixelX = roomOffset.value.x + (x * pixelsPerMeter);
                ctx.value.beginPath();
                ctx.value.moveTo(pixelX, roomOffset.value.y);
                ctx.value.lineTo(pixelX, roomOffset.value.y + roomPixelSize.value * (roomLength.value / roomWidth.value));
                ctx.value.stroke();
            }

            // Draw horizontal lines
            for (let y = 0; y <= roomLength.value; y += gridSize) {
                const pixelY = roomOffset.value.y + (y * pixelsPerMeter);
                ctx.value.beginPath();
                ctx.value.moveTo(roomOffset.value.x, pixelY);
                ctx.value.lineTo(roomOffset.value.x + roomPixelSize.value, pixelY);
                ctx.value.stroke();
            }
        };

        const drawRug = (rug) => {
            // Convert rug centimeters to meters for calculation
            const rugWidthM = cmToMeters(rug.width);
            const rugLengthM = cmToMeters(rug.length);

            // Calculate pixels per meter based on room width
            const pixelsPerMeter = roomPixelSize.value / roomWidth.value;

            // Calculate rug position and dimensions in pixels
            const rugPixelX = roomOffset.value.x + (rug.x * pixelsPerMeter);
            const rugPixelY = roomOffset.value.y + (rug.y * pixelsPerMeter);
            const rugPixelWidth = rugWidthM * pixelsPerMeter;
            const rugPixelHeight = rugLengthM * pixelsPerMeter;

            ctx.value.fillStyle = rug.color;

            switch (props.size?.rug_shape) {
                case 'circle':
                    // Draw circle
                    ctx.value.beginPath();
                    ctx.value.ellipse(
                        rugPixelX + rugPixelWidth / 2, // center X
                        rugPixelY + rugPixelHeight / 2, // center Y
                        rugPixelWidth / 2, // radiusX
                        rugPixelHeight / 2, // radiusY
                        0, // rotation
                        0, // startAngle
                        2 * Math.PI // endAngle
                    );
                    ctx.value.fill();

                    // Draw selection border if selected
                    if (rug === selectedRug.value) {
                        ctx.value.strokeStyle = '#000';
                        ctx.value.lineWidth = 2;
                        ctx.value.stroke();
                    }
                    break;

                case 'square':
                case 'rectangle':
                default:
                    // Draw rectangle
                    ctx.value.fillRect(rugPixelX, rugPixelY, rugPixelWidth, rugPixelHeight);

                    // Draw selection border if selected
                    if (rug === selectedRug.value) {
                        ctx.value.strokeStyle = '#000';
                        ctx.value.lineWidth = 2;
                        ctx.value.strokeRect(rugPixelX, rugPixelY, rugPixelWidth, rugPixelHeight);
                    }
                    break;
            }
        };

        // Rug management functions
        const addRug = (width, length) => {
            console.log('Adding rug:', width, length);

            const rugWidthM = cmToMeters(width);
            const rugLengthM = cmToMeters(length);

            // Create the rug first
            rug.value = {
                x: 0,
                y: 0,
                width: width,
                length: length,
                color: `rgba(100, 100, 100, 0.7)`
            };

            // Auto-adjust room orientation if needed
            autoAdjustRoomOrientation();

            // Always center the rug
            const centerX = Math.max(0, (roomWidth.value - rugWidthM) / 2);
            const centerY = Math.max(0, (roomLength.value - rugLengthM) / 2);

            rug.value.x = centerX;
            rug.value.y = centerY;

            render();
        };

        const rotateRug = () => {
            if (!rug.value) return;

            // Swap rug dimensions
            const tempWidth = rug.value.width;
            rug.value.width = rug.value.length;
            rug.value.length = tempWidth;

            // Auto-adjust room orientation if needed for the rotated rug
            autoAdjustRoomOrientation();

            // Re-center rug with new dimensions
            addRug(rug.value.width, rug.value.length);
        };

        const rotateRoom = () => {
            // Swap room dimensions
            const tempWidth = roomWidth.value;
            roomWidth.value = roomLength.value;
            roomLength.value = tempWidth;

            // Update canvas with new dimensions
            updateCanvasSize();

            // Re-center rug if it exists
            if (rug.value) {
                // Calculate center point first (to preserve position)
                const rugWidthM = cmToMeters(rug.value.width);
                const rugLengthM = cmToMeters(rug.value.length);
                
                // Always center the rug
                const centerX = Math.max(0, (roomWidth.value - rugWidthM) / 2);
                const centerY = Math.max(0, (roomLength.value - rugLengthM) / 2);
    
                rug.value.x = centerX;
                rug.value.y = centerY;
                
                render();
            }
        };

        // Mouse event handlers
        const getMousePos = (e) => {
            const rect = canvas.value.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;

            return {
                x: ((mouseX - roomOffset.value.x) / roomPixelSize.value) * roomWidth.value,
                y: ((mouseY - roomOffset.value.y) / (roomPixelSize.value * (roomLength.value / roomWidth.value))) * roomLength.value
            };
        };

        const handleMouseDown = (e) => {
            const pos = getMousePos(e);

            if (rug.value) {
                const rugWidthM = cmToMeters(rug.value.width);
                const rugLengthM = cmToMeters(rug.value.length);

                // Calculate rug bounds in room coordinates
                const rugBounds = {
                    left: rug.value.x,
                    right: rug.value.x + rugWidthM,
                    top: rug.value.y,
                    bottom: rug.value.y + rugLengthM
                };

                // Check if click is within rug bounds
                if (pos.x >= rugBounds.left &&
                    pos.x <= rugBounds.right &&
                    pos.y >= rugBounds.top &&
                    pos.y <= rugBounds.bottom) {

                    selectedRug.value = rug.value;
                    isDragging.value = true;
                    dragOffset.value = {
                        x: pos.x - rug.value.x,
                        y: pos.y - rug.value.y
                    };
                }
            }
        };

        const handleMouseMove = (e) => {
            if (!isDragging.value || !selectedRug.value) return;

            const pos = getMousePos(e);
            const rugWidthM = cmToMeters(selectedRug.value.width);
            const rugLengthM = cmToMeters(selectedRug.value.length);

            rug.value.x = pos.x - dragOffset.value.x;
            rug.value.y = pos.y - dragOffset.value.y;

            // Keep rug within room bounds
            rug.value.x = Math.max(0, Math.min(rug.value.x, roomWidth.value - rugWidthM));
            rug.value.y = Math.max(0, Math.min(rug.value.y, roomLength.value - rugLengthM));

            render();
        };

        const handleMouseUp = () => {
            isDragging.value = false;
        };

        const updateCursor = (e) => {
            const pos = getMousePos(e);
            if (rug.value) {
                const rugWidthM = cmToMeters(rug.value.width);
                const rugLengthM = cmToMeters(rug.value.length);

                if (pos.x >= rug.value.x &&
                    pos.x <= rug.value.x + rugWidthM &&
                    pos.y >= rug.value.y &&
                    pos.y <= rug.value.y + rugLengthM) {
                    canvas.value.style.cursor = 'move';
                    return;
                }
            }
            canvas.value.style.cursor = 'default';
        };

        // Add function to confirm dimensions
        const confirmDimensions = () => {
            dimensionsOverlay.value = false;
            updateCanvasSize();
            
            // After confirming dimensions, auto-adjust room orientation if needed
            if (rug.value) {
                autoAdjustRoomOrientation();
                // Re-center rug with new dimensions
                addRug(rug.value.width, rug.value.length);
            }
            
            render();
        };

        // Lifecycle hooks and watchers
        onMounted(() => {
            ctx.value = canvas.value.getContext('2d');
            updateCanvasSize();
            window.addEventListener('resize', updateCanvasSize);
            canvas.value.addEventListener('mousemove', updateCursor);
        });

        onUnmounted(() => {
            window.removeEventListener('resize', updateCanvasSize);
            if (canvas.value) {
                canvas.value.removeEventListener('mousemove', updateCursor);
            }
        });

        // Watchers
        watch(() => props.size, (newSize) => {
            if (newSize) {
                addRug(newSize.rug_width, newSize.rug_length);
            }
        }, { immediate: true });

        watch([roomWidth, roomLength], () => {
            if (rug.value) {
                // Auto-adjust room orientation if needed after room size changes
                autoAdjustRoomOrientation();
                addRug(rug.value.width, rug.value.length);
            }
            render();
        });

        watch(
            () => canvas.value?.offsetWidth,
            () => updateCanvasSize(),
            { immediate: true }
        );

        return {
            canvas,
            roomWidth,
            roomLength,
            dimensionsOverlay,
            rug,
            handleMouseDown,
            handleMouseMove,
            handleMouseUp,
            rotateRug,
            rotateRoom,
            cmToMeters,
            confirmDimensions,
            autoAdjusted
        };
    },
    template: `
        <div class="room-planner-container">
            <div class="relative">
                <canvas ref="canvas" class="w-full h-[400px]" 
                    @mousedown="handleMouseDown" 
                    @mousemove="handleMouseMove"
                    @mouseup="handleMouseUp" 
                    @contextmenu.prevent>
                </canvas>
                <div v-if="roomWidth && roomLength && rug && (roomWidth < cmToMeters(rug.width) || roomLength < cmToMeters(rug.length))"
                    class="rug-warning">Warning: Rug exceeds the dimensions of the room</div>
                <div v-if="autoAdjusted" class="auto-adjusted-notice">Room orientation was adjusted to fit the rug</div>
                <div v-if="dimensionsOverlay" class="dimensions-overlay">
                    <div class="dimensions-overlay-content">
                        <div class="mb-2 font-medium text-shades-800">Enter the dimensions of the room:</div>
                        <div class="text-sm text-shades-800">Room Width</div>
                        <input class="w-full mb-2 input-field" type="number" placeholder="Room Width" v-model="roomWidth"
                            @change="render">
                        <div class="text-sm text-shades-800">Room Length</div>
                        <input class="w-full mb-2 input-field" type="number" placeholder="Room Length" v-model="roomLength"
                            @change="render">
                        <button class="w-full btn btn-primary" @click="confirmDimensions">Confirm</button>
                    </div>
                </div>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-2 p-2 bg-shades-50">
                <button @click="rotateRug" class="btn btn-primary btn-icon">
                    <svg width="15" height="15" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                        <path
                            d="M142.9 142.9c-17.5 17.5-30.1 38-37.8 59.8L44.8 181.4C55.6 150.7 73.2 122 97.6 97.6c87.5-87.5 229.3-87.5 316.8 0c0 0 0 0 0 0L472 40l24 24 0 160-160 0-24-24 57.1-57.1s0 0 0 0c-62.5-62.5-163.8-62.5-226.3 0zm0 226.3c62.5 62.5 163.8 62.5 226.3 0c17.5-17.5 30.1-38 37.8-59.8l60.4 21.3c-10.8 30.6-28.4 59.3-52.9 83.8c-87.5 87.5-229.3 87.5-316.7 0c0 0 0 0 0 0L40 472 16 448l0-160 160 0 24 24-57.1 57.1z" />
                    </svg>
                    <div>Rug</div>
                </button>
                <button @click="rotateRoom" class="btn btn-primary btn-icon">
                    <svg width="15" height="15" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                        <path
                            d="M142.9 142.9c-17.5 17.5-30.1 38-37.8 59.8L44.8 181.4C55.6 150.7 73.2 122 97.6 97.6c87.5-87.5 229.3-87.5 316.8 0c0 0 0 0 0 0L472 40l24 24 0 160-160 0-24-24 57.1-57.1s0 0 0 0c-62.5-62.5-163.8-62.5-226.3 0zm0 226.3c62.5 62.5 163.8 62.5 226.3 0c17.5-17.5 30.1-38 37.8-59.8l60.4 21.3c-10.8 30.6-28.4 59.3-52.9 83.8c-87.5 87.5-229.3 87.5-316.7 0c0 0 0 0 0 0L40 472 16 448l0-160 160 0 24 24-57.1 57.1z" />
                    </svg>
                    <div>Room</div>
                </button>
                <button @click="dimensionsOverlay = true" class="btn btn-primary btn-icon">
                    <svg width="15" height="15" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                        <path
                            d="M142.9 142.9c-17.5 17.5-30.1 38-37.8 59.8L44.8 181.4C55.6 150.7 73.2 122 97.6 97.6c87.5-87.5 229.3-87.5 316.8 0c0 0 0 0 0 0L472 40l24 24 0 160-160 0-24-24 57.1-57.1s0 0 0 0c-62.5-62.5-163.8-62.5-226.3 0zm0 226.3c62.5 62.5 163.8 62.5 226.3 0c17.5-17.5 30.1-38 37.8-59.8l60.4 21.3c-10.8 30.6-28.4 59.3-52.9 83.8c-87.5 87.5-229.3 87.5-316.7 0c0 0 0 0 0 0L40 472 16 448l0-160 160 0 24 24-57.1 57.1z" />
                    </svg>
                    <div>Edit Dimensions</div>
                </button>
            </div>
        </div>
    `
};

// Export the component using module.exports
module.exports = { RugCanvas };